import BaseService from '../../base/base_service';
import { SYSTEM_EVENTS } from '../../events/events';
import {TimeSpan} from "@sparkware/app-utils";
import UserActivityDetector from '../oauth/states/user_activity_detector';
import { ILocalizedGameInfo } from '../games_information/games_information_service_interface';
import _ from '../../utils/underscore';
import Store from '../../utils/store';

const MAX_APPEARANCES_PER_SESSION: number = 3;
const ACTIVITY_DETECTION_INTERVAL: TimeSpan = TimeSpan.fromSeconds(1);
const IDLE_DETECTION_INTERVAL: TimeSpan = TimeSpan.fromMinutes(1);
const IDLE_RECOMMENDATIONS_LOCAL_STORAGE_KEY: string = 'idleRecommendationsData';
const DISPLAY_INTERVAL: TimeSpan = TimeSpan.fromSeconds(0);

export default class IdleRecommendationsService extends BaseService {
  static CLASS = 'IdleRecommendationsService';

  private _idleDetectionInterval: TimeSpan;
  private _numberOfAppearancesInSession: number;
  private _maxAppearancesAllowedPerSession: number;
  private _idleDetectionSubscription: any;
  private _userActivityDetector: any;
  private _isIdleRecToasterDisplayed: boolean;
  private _displayInterval: TimeSpan;
  private _recommendedGameType: string | null = null;
  private _toasterDisposer: (() => void) | null = null;

  init(params: any) {
    super.init(params);
  }
  protected shouldApplyFeatureToggleABTesting(): boolean{
    return !this.featureToggle();
  }
  protected toggleFeatureOn(params) {
    this._idleDetectionInterval = params.idleDetectionInterval ? TimeSpan.parse(params.idleDetectionInterval) : IDLE_DETECTION_INTERVAL;
    this._maxAppearancesAllowedPerSession = params.maxAppearancesPerSession ? params.maxAppearancesPerSession : MAX_APPEARANCES_PER_SESSION;
    this._numberOfAppearancesInSession = this._getNoOfAppearancesInSessionFromLocalStorage();
    this._displayInterval = params.displayInterval ? TimeSpan.parse(params.displayInterval) : DISPLAY_INTERVAL;
    this._subscribe();
    this._stopIdleDetection(); // Just to be on the safe side
    this._startIdleDetection();
  }

  private _subscribe() {
    this.onSystemEvent([SYSTEM_EVENTS.OAUTH_STATE_CHANGE, SYSTEM_EVENTS.ON_INIT_COMPLETE], this._startIdleDetection.bind(this));
    this.onSystemEvent(SYSTEM_EVENTS.CLOSE_GAME, this._resetIdleDetectionTimer.bind(this));
    this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._resetAppearancesInSession.bind(this));
    this.onSystemEvent(SYSTEM_EVENTS.GAME_OPEN, this._closeToasterWhenOpeningAnotherGame.bind(this));
  }


  private _startIdleDetection() {
    if (this.serviceFactory.oAuth2Service().isAuthorized()) {
      this._startUserIdleDetection();
      this._startUserActivityDetection();
    } else {
      this._stopIdleDetection();
    }
  }

  private _disposeIdleDetectionTimer() {
    if (this._idleDetectionSubscription) {
      this._idleDetectionSubscription.dispose();
    }
  }

  private _resetIdleDetectionTimer() {
    this._disposeIdleDetectionTimer();
    this._startUserIdleDetection();
  }

  private _closeToasterWhenOpeningAnotherGame(){
    if(this._isIdleRecToasterDisplayed && this._toasterDisposer){
      this._onCloseAnalytics = this._openedAnotherGameWhileIdleIsActiveAnalytics;
      this._toasterDisposer();
      this._toasterDisposer = null;
    }
  }

  private _startUserActivityDetection() {
    this._userActivityDetector = UserActivityDetector.create({
      serviceFactory: this.serviceFactory,
      userActivityDetectionInterval: ACTIVITY_DETECTION_INTERVAL,
      onUserActivityCallback: this._resetIdleDetectionTimer.bind(this),
    });
  }

  private _startUserIdleDetection() {
    this._idleDetectionSubscription = this.serviceFactory
      .observableFactoryService()
      .fromInterval(this._idleDetectionInterval)
      .forEach(() => {
        this._showIdleRecommendationToaster();
      });
  }

  private _stopIdleDetection() {
    this._disposeIdleDetectionTimer();
    if (this._userActivityDetector) this._userActivityDetector.dispose();
    this._isIdleRecToasterDisplayed = false;
  }

  // We have two different types of close analytics: one for clicking the idle rec's X btn and one for launching a game(NOT the one in idle) while the idle is active.
  private _defaultOnCloseAnalytics = () => {
    this._sendAnalytics({action: 'click_close_button', label: this._recommendedGameType});
  };

  private _openedAnotherGameWhileIdleIsActiveAnalytics = () => {
      this._sendAnalytics({action: 'launched_other_game', label: this._recommendedGameType});
  };

  private _openedRecommendedGameAnalytics = () => {
      this._sendAnalytics({action: 'click_play', label: this._recommendedGameType});
  };

  private _onCloseAnalytics = this._defaultOnCloseAnalytics;

  private _onClosed() {
    this._isIdleRecToasterDisplayed = false;
    this._resetIdleDetectionTimer();
    this._onCloseAnalytics();
  }

  private _onOpened() {
    this._isIdleRecToasterDisplayed = true;
  }

  private async _showIdleRecommendationToaster() {
    if (this._isIdleRecToasterDisplayed || this.serviceFactory.gameService().isGameRunning() || this.serviceFactory.overlayManagerService().isActiveOverlayExists()) {
      return;
    }

    if (this._numberOfAppearancesInSession >= this._maxAppearancesAllowedPerSession) {
      this.logger.debug(`IdleRecommendations toaster not displayed - max appearances per session - ${this._maxAppearancesAllowedPerSession} reached`);
      return;
    }

    this._getRecentlyPlayedGame()
      .then(recentlyPlayedGame => this._getRecommendedGameBasedOnCorrelation(recentlyPlayedGame))
      .then(recomGameType => {
        this._recommendedGameType = recomGameType;
        return this._getRecommendedGameInfo(recomGameType);
      })
      .then(recomGameTypeInfo => {
        return this._showToaster(recomGameTypeInfo);
      });
  }

  private async _showToaster(gameInfo: ILocalizedGameInfo): Promise<void> {
    this._onCloseAnalytics = this._defaultOnCloseAnalytics;

    const toasterHandler = this.serviceFactory.toasterProxyService().showIdleRecommendationToast({
      toastOptions: {
        timeout: TimeSpan.fromSeconds(this._displayInterval.totalSeconds),
        hideProgressBar: true,
        className: "cgp-toaster-component"
      },
      contentProps: {
        gameType: gameInfo.gameType,
        onPlayRecommendedGameHandler: () => {
          if(this._isIdleRecToasterDisplayed && this._toasterDisposer) {
            this._onCloseAnalytics = this._openedRecommendedGameAnalytics;
            this._toasterDisposer();
            this._toasterDisposer = null;
          }
          this.serviceFactory.gameRunnerProxyService().openRealGameFromSdkRequest(gameInfo.gameType);
        }
      },
      onToastShown: () => {
        this._onOpened();
        this._increaseAppearancesInSession();
        this._sendAnalytics({action: 'view', label: this._recommendedGameType, value: 1 + this._numberOfAppearancesInSession});
      },
      onToastClosed: () => {
        this._onClosed();
      }
    });
    this._toasterDisposer = toasterHandler.close;

  }

  private async _getRecentlyPlayedGame(): Promise<number> {
    const recentlyGamesPlayed = await this.serviceFactory.recentlyPlayedService().getRecentlyPlayedList();
    if (recentlyGamesPlayed.length === 0) {
      const { aiGameTypesArray } = (await this.serviceFactory.aiListService().getAIList()) || Object.assign({});

      if (_.isArray(aiGameTypesArray) && aiGameTypesArray.length > 0) return aiGameTypesArray[Math.floor(Math.random() * aiGameTypesArray.length)];
      else {
        this.logger.debug(`IdleRecommendations toaster - not displayed because we didnt get any ai list game`);
        return Promise.reject(false);
      }
    } else {
      return recentlyGamesPlayed[0];
    }
  }

  private async _getRecommendedGameBasedOnCorrelation(recentlyGamePlayed: number): Promise<any> {
    const correlatedGames = await this.serviceFactory.gamesCorrelationService().getCorrelatedGames(recentlyGamePlayed, 6);
    if (_.isArray(correlatedGames) && correlatedGames.length === 0) {
      this.logger.debug(`IdleRecommendations toaster - not displayed because we didnt get correlated games for game type ${recentlyGamePlayed}`);
      return Promise.reject(false);
    }
    return correlatedGames[Math.floor(Math.random() * correlatedGames.length)];
  }

  private async _getRecommendedGameInfo(gameType: number): Promise<ILocalizedGameInfo> {
    const gameDataInfo = await this.serviceFactory.gamesInformationService().getGamesInfo([gameType]);
    if (!gameDataInfo) {
      this.logger.debug(`IdleRecommendations toaster - not displayed because we didnt get game infos for gameType: ' ${gameType}`);
      return Promise.reject(false);
    }
    return gameDataInfo[gameType];
  }

  private _setIdleRecommendationDataInLocalStorage(): void {
    Store.set(IDLE_RECOMMENDATIONS_LOCAL_STORAGE_KEY, JSON.stringify({ noOfAppearancesInSession: this._numberOfAppearancesInSession || 0 }));
  }

  private _getNoOfAppearancesInSessionFromLocalStorage(): number {
    const { noOfAppearancesInSession } = _.safeJSON(Store.get(IDLE_RECOMMENDATIONS_LOCAL_STORAGE_KEY), {}) || Object.assign({});
    return noOfAppearancesInSession || 0;
  }

  private _resetAppearancesInSession(): void {
    this._numberOfAppearancesInSession = 0;
    this._setIdleRecommendationDataInLocalStorage();
  }

  private _increaseAppearancesInSession(): void {
    this._numberOfAppearancesInSession++;
    this._setIdleRecommendationDataInLocalStorage();
  }

  private _sendAnalytics(analyticsData): void{
    this.serviceFactory.analyticsService().sendClickstreamEvent(
        {
          eventName:'Orbit_AI_Feature',
          category: 'idleRecommendations',
          action: analyticsData?.action,
          label: analyticsData?.label,
          value: analyticsData?.value
        });
  }
}
